import { DOCUMENT } from '@angular/common';
import {
  ApplicationRef,
  ComponentRef,
  EnvironmentInjector,
  Injectable,
  OnDestroy,
  createComponent,
  inject,
} from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { OnboardingMarkerEnum } from '../../enums';
import { HintCardComponent } from '../../components/onboarding/hint-card/hint-card.component';
import {
  calculateTooltipPlacement,
  clampTooltipToViewport,
  CustomTooltipPosition,
} from '../../directives/custom-tooltip/custom-tooltip-positioning.util';
import { OnboardingService } from '../requests/onboarding.service';
import { HINT_CONFIG } from './hint.config';

export type HintPosition = 'top' | 'bottom' | 'left' | 'right';

const HINT_OVERLAY_LAYOUT = { gap: 12, padding: 8 } as const;
// Must match $hint-card-z-index in hint-card.component.scss
const HINT_CARD_Z_INDEX = '1100';

export interface HintCardConfig {
  titleKey: string;
  textKey: string;
  iconName: string;
  iconWidth?: number;
  iconHeight?: number;
  position: HintPosition;
}

interface OpenState {
  marker: OnboardingMarkerEnum;
  anchorEl: HTMLElement;
  position: HintPosition;
}

@Injectable({ providedIn: 'root' })
export class HintOverlayService implements OnDestroy {
  private componentRef: ComponentRef<HintCardComponent> | null = null;
  private hostEl: HTMLDivElement | null = null;
  private clickListener: ((e: MouseEvent) => void) | null = null;
  private keyListener: ((e: KeyboardEvent) => void) | null = null;
  private scrollCleanups: (() => void)[] = [];
  private closedSub: Subscription | null = null;
  private currentState: OpenState | null = null;

  private closeSrc$ = new Subject<OnboardingMarkerEnum>();
  readonly close$ = this.closeSrc$.asObservable();

  private readonly environmentInjector = inject(EnvironmentInjector);
  private readonly appRef = inject(ApplicationRef);
  private readonly onboarding = inject(OnboardingService);
  private readonly document = inject(DOCUMENT);
  private readonly autoOpenedMarkers = new Set<OnboardingMarkerEnum>();

  clearAutoOpened(): void {
    this.destroyCard();
    this.autoOpenedMarkers.clear();
  }

  openDirect(marker: OnboardingMarkerEnum, anchorEl: HTMLElement): void {
    if (this.componentRef) return;

    const visible = this.onboarding.getVisibleMarkers();
    if (!visible.has(marker)) return;

    if (this.autoOpenedMarkers.has(marker)) return;

    const config = HINT_CONFIG[marker];
    if (!config) return;

    this.autoOpenedMarkers.add(marker);
    this.open(marker, anchorEl, config.position, config);
  }

  open(
    marker: OnboardingMarkerEnum,
    anchorEl: HTMLElement,
    position: HintPosition,
    config: HintCardConfig,
  ): void {
    this.destroyCard();

    this.currentState = { marker, anchorEl, position };

    const hostEl = this.document.createElement('div');
    hostEl.style.position = 'fixed';
    hostEl.style.zIndex = HINT_CARD_Z_INDEX;
    this.document.body.appendChild(hostEl);
    this.hostEl = hostEl;

    const ref = createComponent(HintCardComponent, {
      environmentInjector: this.environmentInjector,
      hostElement: hostEl,
    });

    ref.setInput('titleKey', config.titleKey);
    ref.setInput('textKey', config.textKey);
    ref.setInput('iconName', config.iconName);
    if (config.iconWidth) ref.setInput('iconWidth', config.iconWidth);
    if (config.iconHeight) ref.setInput('iconHeight', config.iconHeight);

    this.closedSub = ref.instance.closed.subscribe(() => this.close(marker));

    this.appRef.attachView(ref.hostView);
    this.componentRef = ref;
    ref.changeDetectorRef.detectChanges();

    // Position card after rendering so we have dimensions
    requestAnimationFrame(() => {
      const cardEl = hostEl.querySelector('.hint-card') as HTMLElement;
      if (cardEl) {
        const hostRect = anchorEl.getBoundingClientRect();
        const tooltipRect = cardEl.getBoundingClientRect();
        const win = this.document.defaultView;
        if (!win) return;
        const viewportWidth = win.innerWidth;
        const viewportHeight = win.innerHeight;

        const { position: finalPosition, coords } = calculateTooltipPlacement({
          hostRect,
          tooltipRect,
          viewportWidth,
          viewportHeight,
          preferredPosition: position as CustomTooltipPosition,
          ...HINT_OVERLAY_LAYOUT,
        });
        const { top, left } = clampTooltipToViewport({
          coords,
          tooltipRect,
          viewportWidth,
          viewportHeight,
          ...HINT_OVERLAY_LAYOUT,
        });

        hostEl.style.top = `${top}px`;
        hostEl.style.left = `${left}px`;
        cardEl.classList.add(finalPosition as HintPosition);

        // Adjust arrow to track anchor center after horizontal clamping
        const placedCard = cardEl.getBoundingClientRect();
        const anchorCenterX = hostRect.left + hostRect.width / 2;
        const placedCardCenterX = placedCard.left + placedCard.width / 2;
        const arrowOffset = anchorCenterX - placedCardCenterX;
        const ARROW_EDGE_GUARD = 16;
        const maxOffset = placedCard.width / 2 - ARROW_EDGE_GUARD;
        const clampedOffset = Math.max(-maxOffset, Math.min(arrowOffset, maxOffset));
        cardEl.style.setProperty('--arrow-offset', `${clampedOffset}px`);
      }
    });

    this.addListeners(hostEl, anchorEl);
  }

  close(marker: OnboardingMarkerEnum): void {
    this.destroyCard();
    this.closeSrc$.next(marker);
  }

  dismiss(): void {
    if (this.currentState) {
      this.close(this.currentState.marker);
    } else {
      this.destroyCard();
    }
  }

  private addListeners(hostEl: HTMLDivElement, anchorEl: HTMLElement): void {
    this.clickListener = (e: MouseEvent) => {
      if (hostEl.contains(e.target as Node)) return;
      this.dismiss();
    };
    this.keyListener = (e: KeyboardEvent) => {
      if (e.key === 'Escape') this.dismiss();
    };

    // Defer so the click that opened the card doesn't immediately close it
    setTimeout(() => {
      this.document.addEventListener('click', this.clickListener!, true);
      this.document.addEventListener('keydown', this.keyListener!);
      this.addScrollListeners(anchorEl);
    }, 0);
  }

  private addScrollListeners(anchorEl: HTMLElement): void {
    const win = this.document.defaultView;
    if (!win) return;

    const handler = () => {
      if (this.currentState) this.destroyCard();
    };

    let el: HTMLElement | null = anchorEl.parentElement;
    while (el) {
      const { overflow, overflowX, overflowY } = win.getComputedStyle(el);
      if (/auto|scroll/.test(`${overflow}${overflowX}${overflowY}`)) {
        el.addEventListener('scroll', handler, { passive: true });
        const captured = el;
        this.scrollCleanups.push(() => captured.removeEventListener('scroll', handler));
      }
      el = el.parentElement;
    }
  }

  private destroyCard(): void {
    if (this.scrollCleanups.length) {
      this.scrollCleanups.forEach((cleanup) => cleanup());
      this.scrollCleanups = [];
    }
    if (this.closedSub) {
      this.closedSub.unsubscribe();
      this.closedSub = null;
    }
    if (this.clickListener) {
      this.document.removeEventListener('click', this.clickListener, true);
      this.clickListener = null;
    }
    if (this.keyListener) {
      this.document.removeEventListener('keydown', this.keyListener);
      this.keyListener = null;
    }
    if (this.componentRef) {
      this.appRef.detachView(this.componentRef.hostView);
      this.componentRef.destroy();
      this.componentRef = null;
    }
    if (this.hostEl) {
      this.hostEl.remove();
      this.hostEl = null;
    }
    this.currentState = null;
  }

  ngOnDestroy(): void {
    this.destroyCard();
  }
}
